import indexedDbRepo from "./ordersIndexedDbRepository";
import cashboxIndexedDb from "../cashboxes/cashboxesIndexedDBRepository";
import {
  getTotalAmountFromProduct,
  getTotalVatAmountFromProduct,
  getAmountFromProduct,
  getDiscountsFromProductForSync,
  getProductTotalDiscountAmountForSync,
  getProductTotalDiscountAmount,
} from "../products/productsService";
import axios from "axios";
import { formatDate, getAndroidIdFromLocalStorage, isOlderThan} from "./../../utils/utils";
import { ASYNC_STATUS, ORDER_STATUS, ORDER_TYPE } from "./../../utils/orderStatuses";
import { toast } from "vue3-toastify";
import { currency } from "@/filters/currency";
import { parseToQuery } from "@/utils/utils";


const getOrderTotalAmount = (order) => {
  return order.amount;
};

const getOrderByUuid = async (shopCode, orderUuid) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return null;
  }
  const orders = await indexedDbRepo.getOrders(shopCode)
  const order = orders.find((x) => x.uuid == orderUuid)
  return order;
}

const getReprintBillTicket = async (orderUuid, billType, shopCode) => {
  const params = {
    "billType": billType,
    "internalReference": orderUuid,
    "shopCode": shopCode,
  }

  const ticketXmlString = await axios.get(`/api/ticketsViewer/getBillTickets/?${parseToQuery(params)}`)
  return ticketXmlString.data.templateTicket[0]
}

const getTotalDiscountAmountOfAllProductsInTheOrder = (order) => {
  return (
    order.products?.reduce(
      (acc, prod) => acc + getProductTotalDiscountAmount(prod),
      0
    ) ?? 0
  );
};

const getOrderComment = (order) => {
  if (order.isTableOrder) {
    return `${order.tableName} - N° ${order.ticketNumber}`;
  }
  if ([ORDER_TYPE.VOID, 
      ORDER_TYPE.VOID_OLD, 
      ORDER_TYPE.VOID_NT, 
      ORDER_TYPE.VOID_NT_OLD, 
      ORDER_TYPE.VOID_BY_BISTRO,
      ORDER_TYPE.VOID_BY_BISTRO_OLD,
    ].includes(order.movementType)) {
    return order.comments
  } 
  return `${order.origin} N° ${order.ticketNumber}`;
};
const getOrderTotalVatAmount = (order) => {
  return parseFloat(order.vatAmount.toFixed(2));
};

const getOrder = async (shopCode, orderId) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return null;
  }
  const orders = await indexedDbRepo.getOrders(shopCode);
  const order = orders.filter((x) => x.id == orderId)[0];
  return order;
};

const getOrders = async (shopCode) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return [];
  }
  return await indexedDbRepo.getOrders(shopCode);
};
const getActiveOrders = async (shopCode) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return [];
  }
  const allOrders = await indexedDbRepo.getOrders(shopCode);
  return allOrders.filter((order) =>
    order.status == ORDER_STATUS.ACTIVE &&
    order.isTableOrder !== true
  );
};

const getOrderByTableId = async (shopCode, tableId) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return null;
  }
  const allOrders = await indexedDbRepo.getOrders(shopCode);
  return allOrders.find((order) =>
    order.status == ORDER_STATUS.ACTIVE &&
    order.tableId == tableId &&
    order.isTableOrder === true
  );
};

const createOrder = async (shopCode, tableId = null, tableName = null, guestCount = null) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return null;
  }
  const order = {
    uuid: window.crypto.randomUUID(),
    origin: "Mostrador",
    status: ORDER_STATUS.ACTIVE,
    createdAt: Date.now(),
    amount: 0,
    netAmount: 0,
    vatAmount: 0,
    products: [],
    asyncStatus: ASYNC_STATUS.PENDING,
    payments: [],
    lastUpdate: Date.now(),
    ticketNumber: null,
    movementType: ORDER_TYPE.COMANDA,
    BillNumber: null,
    discounts: [],
    tableId: tableId,
    tableName: tableName,
    isTableOrder: tableId ? true : false,
    guestCount: guestCount,
    isPaid: false,
  };
  return await indexedDbRepo.insertOrder(shopCode, order);
};

const createSaleOrder = async (shopCode, originOrder) => {
  const order = {
    uuid: originOrder.uuid,
    origin: "Mostrador",
    status: ORDER_STATUS.CLOSED,
    createdAt: Date.now(),
    amount: originOrder.amount,
    netAmount: originOrder.netAmount,
    vatAmount: originOrder.vatAmount,
    products: originOrder.products,
    asyncStatus: ASYNC_STATUS.PENDING,
    payments: originOrder.payments,
    lastUpdate: Date.now(),
    timeStamp: formatDate(),
    ticketNumber: originOrder.ticketNumber,
    movementType: ORDER_TYPE.VENTA,
    BillNumber: originOrder.relatedFiscalInfo.billNumber,
    discounts: originOrder.discounts,
    relatedFiscalInfo: originOrder.relatedFiscalInfo
  };
  return await indexedDbRepo.insertOrder(shopCode, order);
};

const createVoidIndexDB = async (shopCode, modalInfoOrder, orderCurrentBox, t) => {
  const order = await getOrderByUuid(shopCode, modalInfoOrder.uuid)
  
  order.timeStamp = formatDate()
  order.lastUpdate = Date.now()
  const cashBoxOrder = {...order}
  cashBoxOrder.movementType = isCurrentOrPreviousCashRegister(orderCurrentBox, ORDER_TYPE.VOID)
  cashBoxOrder.payments[0].paymentWay = modalInfoOrder.voidPaymentInfo
  cashBoxOrder.amount = cashBoxOrder.amount* - 1
  cashBoxOrder.comments = `ANULACION DE COMANDA N° ${cashBoxOrder.ticketNumber}`
  try {
    await createObjectCashbox(shopCode, cashBoxOrder, t)
    

    order.asyncStatus = ASYNC_STATUS.PENDING
    order.status = isCurrentOrPreviousCashRegister(orderCurrentBox, ORDER_TYPE.VOID)
    await updateOrder(shopCode, order);
  } catch (error) {
    toast.error(`Error al sincronizar la orden ${order.id}`);
  }
}

const createVoidSaleIndexDB = async (shopCode, modalInfoOrder, orderCurrentBox, t, data) => {
  const order = await getOrderByUuid(shopCode, modalInfoOrder.uuid)
  
  order.timeStamp = formatDate()
  order.lastUpdate = Date.now()
  order.relatedFiscalInfo = {
    billType: modalInfoOrder.billType,
    billNumber: data.responseMessage,
    customerName: modalInfoOrder.relatedFiscalInfo.customerName || "",
    customerFiscalKey: modalInfoOrder.relatedFiscalInfo.customerFiscalKey,
    customerVatCondition: modalInfoOrder.relatedFiscalInfo.customerVatCondition,
    customerAddress: ""
  }
  order.BillNumber = order.relatedFiscalInfo.billNumber
  const cashBoxOrder = {...order}
  cashBoxOrder.movementType = isCurrentOrPreviousCashRegister(orderCurrentBox, ORDER_TYPE.VOID_NT)
  cashBoxOrder.payments[0].paymentWay = modalInfoOrder.voidPaymentInfo
  cashBoxOrder.amount = cashBoxOrder.amount* - 1
  cashBoxOrder.vatAmount = cashBoxOrder.vatAmount* - 1
  cashBoxOrder.comments = `Anulacion por Nota de Credito N° ${cashBoxOrder.ticketNumber}`
  try {
    await createObjectCashbox(shopCode, cashBoxOrder, t, `${order.relatedFiscalInfo.billType} ${order.relatedFiscalInfo.billNumber}`)
    

    order.asyncStatus = ASYNC_STATUS.PENDING
    order.status = isCurrentOrPreviousCashRegister(orderCurrentBox, ORDER_TYPE.VOID_NT)
    await updateOrder(shopCode, order);
  } catch (error) {
    toast.error(`Error al sincronizar la orden ${order.id}`);
  }
}

const createObjectCashbox = async (shopCode, order, t, billNumber = null) => {
  const cashbox = {
    amount: getOrderTotalAmount(order),
    asyncStatus: ASYNC_STATUS.PENDING,
    billNumber: billNumber,
    cashAccumAmount: 0,
    cashMovementId: order.id,
    comments: getOrderComment(order),
    date: formatDate(),
    ticketNumber: String(order.ticketNumber),
    movementType: order.movementType,
    totalAccumDiscountPerProductAmount:
      getTotalDiscountAmountOfAllProductsInTheOrder(order),
    saleAuthCode: order.payments[0]?.authCode,
    saleCaptureWay: order.payments[0]?.captureWay,
    saleOrigin: order.origin.toUpperCase(),
    salePaymentMethod: order.payments[0]?.paymentWay,
    saleType: order.movementType,
    saleId: order.id,
    sequence: 1,
    totalAccumAmount: 0,
    totalDiscountAmount:
      order.discounts?.reduce((acc, discount) => acc + discount, 0) ?? 0,
    totalTaxesAmount: 0,
    totalVatAmount: getOrderTotalVatAmount(order),
    userName: t.$store.authUser?.user?.name,
    uuid: order.uuid,
    createdAt: Date.now(),
    waiter: null,
    tableName: order.tableName || null,
    isTableOrder: order.isTableOrder || false,
    guestCount: order.guestCount || 0,
    txOpened: order.isTableOrder ? order.createdAt : null,
    txClosed: order.isTableOrder ? order.txClosed : null
  };
  return await cashboxIndexedDb.insertCashbox(shopCode, cashbox);
};
const getDiscountsForBilling = (order) => { 
  // BS-6372: los descuentos POR PRODUCTO ahora viajan por item (BillItems[].Discounts en generateBillItems),
  // igual que Android. Aca quedan solo los descuentos GLOBALES de la orden.
  const orderDiscounts = order.discounts || []; // Evitamos errores si `order.discounts` es `undefined`
  const validDiscounts = orderDiscounts.filter(discount => discount && discount.type && discount.amount);
  const simplifiedDiscounts = validDiscounts.map(discount => ({
    Type: String(discount.type),
    Amount: parseFloat(discount.amount)
  }));
  return simplifiedDiscounts 
}
const processBilling = async (shopCode, modalInfoOrder, t, orderBelongToCurrentBox) => {
  if(process.env.VUE_APP_INSTANCE_CODE != "AR") {
    toast.error("No se puede facturar en este país.");
    return;
  }
  let order = await getOrderByUuid(shopCode, modalInfoOrder.uuid)


  const billingRequest ={
    ShopCode: shopCode,
    AndroidId: getAndroidIdFromLocalStorage(shopCode),
    BillType: modalInfoOrder.billType,
    FiscalKey:  getFiscalKey(modalInfoOrder.billingData),
    OrderNumber: order?.ticketNumber?.toString(),
    FiscalDocumentType: "",
    FiscalCondition: getFiscalCondition(modalInfoOrder.billingData.selectedVatCondition),
    FiscalLegalName: modalInfoOrder.billingData.legalName ? modalInfoOrder.billingData.legalName : "",
    FiscalFantasyName: "",
    FiscalAddress: "",
    FiscalCity: "",
    FiscalProvince: "",
    PaymentWay: modalInfoOrder.payments[0].paymentWay,
    InternalReference: window.crypto.randomUUID(),
    OriginalOrderInternalReference: modalInfoOrder.uuid,
    BillItems: generateBillItems(modalInfoOrder),
    Discounts: getDiscountsForBilling(modalInfoOrder),
    Taxes: [],
    FiscalEmail: null,
    FiscalBillUse: null,
    IsBillingFromValidatorService: false
  }
  try {
    const {data} = await axios.post(`/api/pdv/billing`, billingRequest)
    if (data?.responseCode == 0) {
      let orderVoidByBistroToCashbox = {...order}
      let saleOrderToCashbox = {...order}
      let saleOrder = {...order}
      if(order){
          orderVoidByBistroToCashbox.movementType = isCurrentOrPreviousCashRegister(orderBelongToCurrentBox, ORDER_TYPE.VOID_BY_BISTRO)
          orderVoidByBistroToCashbox.amount = order.amount* - 1
          orderVoidByBistroToCashbox.vatAmount = order.vatAmount* - 1
          orderVoidByBistroToCashbox.comments = `ANULACION POR FACT # ${order.ticketNumber}` 
          await createObjectCashbox(shopCode, orderVoidByBistroToCashbox, t)

          saleOrder.uuid = billingRequest.InternalReference
          saleOrder.relatedFiscalInfo = {
            billType: modalInfoOrder.billType,
            billNumber: data.responseMessage,
            customerName: billingRequest.FiscalLegalName || "",
            customerFiscalKey: billingRequest.FiscalKey,
            customerAddress: "",
            customerVatCondition: billingRequest.FiscalCondition
          }
          saleOrder.BillNumber = saleOrder.relatedFiscalInfo.billNumber
          const newSaleOrder = await createSaleOrder(shopCode, saleOrder)

          saleOrderToCashbox.movementType = ORDER_TYPE.VENTA
          saleOrderToCashbox.uuid = billingRequest.InternalReference
          // BPM-16: la VENTA debe llevar el id de la venta nueva, no el de la comanda anulada.
          // Si comparte saleId con el VOID_BY_BISTRO, el backend la purga del Detalle de Transacciones.
          saleOrderToCashbox.id = newSaleOrder?.id ?? saleOrderToCashbox.id
          await createObjectCashbox(shopCode, saleOrderToCashbox, t, `${modalInfoOrder.billType} ${data.responseMessage}`)

          order.asyncStatus = ASYNC_STATUS.PENDING
          order.status = isCurrentOrPreviousCashRegister(orderBelongToCurrentBox, ORDER_TYPE.VOID_BY_BISTRO)
          order.timeStamp = formatDate()
          order.lastUpdate = Date.now()
          await updateOrder(shopCode, order)
          if(data.templateTicket){
            const ticket = replaceItemsTicket(data.templateTicket, order)
            return ticket
          }
          toast.error('Ticket no disponible. Por favor, reimprima más tarde.');
        } 
      else {
        toast.error("Ocurrió un error, no se encontró la orden.");

      }
    }
    else {
      toast.error(data.responseMessage);
    }
  } catch (error) {
    toast.error("Ocurrió un error al sincronizar la factura.");
  }

  return null;
}
const replaceItemsTicket = (ticketXmlString, order) => {
  if (typeof ticketXmlString !== 'string') return '';

  return ticketXmlString
    .replace(/(al Consumidor )/g, "$1\n")
    .replace(/{ticketNumber}/g, order?.ticketNumber ?? '')
    .replace(/{delivered}/g, currency(order?.payments?.[0]?.receivedPaidAmount ?? 0))
    .replace(/{returned}/g, currency(
      (order?.payments?.[0]?.receivedPaidAmount ?? 0) -
      (order?.payments?.[0]?.amount ?? 0)
    ));
};

const getFiscalKey = (billingData) => {
  switch (billingData.selectedBill) {
    case "FCA":
      return billingData.numberDocument;
    case "FCB":
      return billingData.selectedBillingType === "Ticket" ? "11111111" : billingData.numberDocument;
    case "FCC":
      return billingData.selectedBillingType === "Ticket" ? "11111111" : billingData.numberDocument;
  }
}
const getFiscalCondition = (fiscalCondition) => {
  switch (fiscalCondition) {
    case "registeredTaxPayer":
      return "RI";
    case "exempt":
      return "EX";
    case "smallTaxPayer":
      return "MO";
    default:
      return "CF";
  }
}

const processVoidBilling = async (shopCode, modalInfoOrder, t, orderBelongToCurrentBox) => {

  if(process.env.VUE_APP_INSTANCE_CODE != "AR") {
    toast.error("No se puede facturar en este país.");
    return;
  }
  let order = await getOrderByUuid(shopCode, modalInfoOrder.uuid)
  
  const billingRequest ={
    ShopCode: shopCode,
    AndroidId: getAndroidIdFromLocalStorage(shopCode),
    OrderNumber: modalInfoOrder.ticketNumber.toString(),
    BillType: modalInfoOrder.billType,
    FiscalKey: modalInfoOrder.relatedFiscalInfo.customerFiscalKey,
    FiscalDocumentType: "",
    FiscalCondition: modalInfoOrder.relatedFiscalInfo.customerVatCondition,
    FiscalLegalName: modalInfoOrder.relatedFiscalInfo.customerName || "",
    FiscalFantasyName: "",
    FiscalAddress: "",
    FiscalCity: "",
    FiscalProvince: "",
    PaymentWay: modalInfoOrder.payments[0].paymentWay,
    InternalReference: modalInfoOrder.uuid,
    OriginalOrderInternalReference: modalInfoOrder.uuid,
    BillItems: generateBillItems(modalInfoOrder),
    Discounts: getDiscountsForBilling(modalInfoOrder),
    Taxes: [],
    FiscalEmail: null,
    FiscalBillUse: null,
    IsBillingFromValidatorService: false
  }

  try {
    const {data} = await axios.post(`/api/pdv/billing`, billingRequest)
    if (data?.responseCode == 0) {
      await createVoidSaleIndexDB(shopCode, modalInfoOrder, orderBelongToCurrentBox, t, data)
      if(data.templateTicket){
        const ticket = replaceItemsTicket(data.templateTicket, order)
        return ticket
      }
      toast.error('Ticket no disponible. Por favor, reimprima más tarde.');
    } else {
      toast.error(data.responseMessage);
    }
  } catch (error) {
    toast.error("Ocurrió un error al sincronizar la factura.");
    
  } 

  return null;
}

const isCurrentOrPreviousCashRegister = (orderBelongToCurrentBox, action) => {  
  const isCurrent = orderBelongToCurrentBox ? "" : "_OLD";

  switch (action) {
    case ORDER_TYPE.VOID:
      return ORDER_STATUS[`VOID${isCurrent}`];

    case ORDER_TYPE.VOID_BY_BISTRO:
      return ORDER_STATUS[`VOID_BY_BISTRO${isCurrent}`];

    case ORDER_TYPE.VOID_NT:
      return ORDER_STATUS[`VOID_NT${isCurrent}`];

    default:
      throw new Error(`Acción no válida: ${action}`);
  }
}

const generateBillItems = (order) => {
  if (!order || !Array.isArray(order.products)) {
    console.warn("Orden inválida o sin productos.");
    return [];
  }

  return order.products.map(product => ({
    Name: product.name,
    UnitPrice: parseFloat(product.originPrice), //.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    Vat: parseFloat(product.originIva), 
    Quantity: product.quantity,
    DiscountAmount: 0.0,
    Discounts: (getDiscountsFromProductForSync(product) || []).map(d => ({
      Type: String(d.type),
      Amount: parseFloat(d.amount),
      Percentage: d.percentage ? parseFloat(d.percentage) : 0,
      Description: d.description
    })),
    Exempt: product.exempt || false,
    NonTaxed: product.nonTaxed || false
  }));
};

const updateOrder = async (shopCode, order) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return;
  }
  
  // Agregar timestamp de cierre para mesas cuando cambian a CLOSED
  if (order.isTableOrder && order.status === ORDER_STATUS.CLOSED && !order.txClosed) {
    order.txClosed = Date.now();
  }
  
  await indexedDbRepo.updateOrder(shopCode, order);
};

const updateCashbox = async (shopCode, box) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return;
  }
  await cashboxIndexedDb.updateCashbox(shopCode, box);
};

const deleteOrder = async (shopCode, order) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return;
  }
  await indexedDbRepo.deleteOrder(shopCode, order);
};

const deleteCashbox = async (shopCode, box) => {
  if (!shopCode) {
    console.warn("shopCode is required");
    return;
  }
  await cashboxIndexedDb.deleteCashbox(shopCode, box);
};

const syncOrder = async (shopCode, pendingOrder, t) => {
  if (!navigator.onLine) {
    return false;
  }

  const androidId = getAndroidIdFromLocalStorage(shopCode);
  const payload = {
    androidId: androidId,
    iMEI: "",
    Sales: [
      {
        combosDetails: [],
        dinners: pendingOrder.guestCount || 0,
        discounts: pendingOrder.discounts,
        ticketNumber: String(pendingOrder.ticketNumber),
        Origin: pendingOrder.origin,
        fiscalInfo: getFiscalInfoFromOrder(pendingOrder),
        paymentInfo: [
          {
            change: parseFloat(
              (pendingOrder.payments[0]?.receivedPaidAmount ?? 0) -
                pendingOrder.payments[0]?.amount
            ),
            partialAmount: pendingOrder.payments[0]?.amount,
            paymentDescription:
              pendingOrder.payments[0]?.paymentDescription ?? "-",
            paymentWay: pendingOrder.payments[0]?.paymentWay,
            captureWay: pendingOrder.payments[0]?.captureWay,
            authCode: pendingOrder.payments[0]?.authCode,
            cardholder: pendingOrder.payments[0]?.cardHolder,
            truncatedCardNumber: pendingOrder.payments[0]?.truncatedCardNumber,
            externalReferenceId: pendingOrder.payments[0]?.externalReferenceId,
            receivedPaidAmount: parseFloat(
              pendingOrder.payments[0]?.receivedPaidAmount ?? 0
            ),
          },
        ],
        saleDetails: pendingOrder.products.map((product) => ({
          amount: getAmountFromProduct(product),
          exempt: product.exempt ?? false,
          isWeightable: product.weightable,
          nonTaxed: product.nonTaxed ?? false,
          measureUnit: product.measureUnit,
          productCategory: product.department,
          productName: product.name,
          quantity: product.quantity,
          sku: product.sku,
          originPrice: product.originPrice,
          originIva: product.originIva,
          discountAmount: getProductTotalDiscountAmountForSync(product),
          discounts: getDiscountsFromProductForSync(product),
          syncSaleOptionList: [],
          totalAmount: getTotalAmountFromProduct(product),
          totalVatAmount: getTotalVatAmountFromProduct(product),
          vatRate: parseFloat(product.originIva),
          itemAdded: product.itemAdded,
          itemGrossAmount: product.quantity * getAmountFromProduct(product),
        })),
        saleId: pendingOrder.id,
        SaleTaxes: [],
        status: pendingOrder.status,
        timeStamp: pendingOrder.timeStamp, //FIXME #6 -> La fecha debiera estar en la tabla orders.. y sacarla de ahi, como hiciste en cashboxes
        totalAmount: getOrderTotalAmount(pendingOrder),
        totalVatAmount: getOrderTotalVatAmount(pendingOrder),
        username: t.$store.authUser?.user?.name,
        UUID: pendingOrder.uuid,
      },
    ],
  };
  try {
    const { data } = await axios.post(`/api/pdv/SyncSales`, payload);
    if (data?.responseCode == 0) {
      return true;
    } else {
      toast.error(data.message);
      return false;
    }
  } catch (error) {
    toast.error("Error al procesar la orden:", error);
    return false;
  }
};

const getFiscalInfoFromOrder = (pendingOrder) => {
  if(pendingOrder.relatedFiscalInfo)
  return {
    TicketNumber: pendingOrder.relatedFiscalInfo?.billNumber,
    Type:   pendingOrder.relatedFiscalInfo?.billType
  };
  else return null;
}


const syncCashbox = async (shopCode, box) => {
  const androidId = getAndroidIdFromLocalStorage(shopCode);
  const payload = {
    androidId: androidId,
    // shopCode: shopCode, //No poner de modo que asigne por androidId.. Paso que con un andoirId deshabilitado igual anoto en DB en cashbox solo con el shopcode.
    CashBox: [
      {
        Amount: box.amount,
        BillNumber: box.billNumber,
        CashAccumAmount: box.cashAccumAmount,
        CashMovementId: box.id,
        Comments: box.comments,
        Date: box.date,
        ticketNumber: box.ticketNumber,
        MovementType: box.movementType,
        TotalAccumDiscountPerProductAmount:
          box.totalAccumDiscountPerProductAmount,
        SaleAuthCode: box.saleAuthCode,
        SaleCaptureWay: box.captureWay,
        SaleId: box.saleId,
        SaleOrigin: box.saleOrigin,
        SalePaymentMethod: box.salePaymentMethod,
        SaleType: box.saleType,
        sequence: box.sequence,
        TotalAccumAmount: box.totalAccumAmount,
        TotalDiscountAmount: box.totalDiscountAmount,
        TotalTaxesAmount: box.totalTaxesAmount,
        TotalVatAmount: box.totalVatAmount,
        UserName: box.userName,
        UUID: box.uuid,
        customerId: box.customerId,
        externalReference: box.externalReference,
        waiter: box.waiter,
        tableName: box.tableName,
        txOpened: box.isTableOrder ? formatDate(box.txOpened) : null,
        txClosed: box.isTableOrder ? formatDate(box.txClosed) : null,
      },
    ],
    iMEI: "",
  };
  try {
    const data = await axios.post(`/api/pdv/SyncCashbox`, payload);
    if (data?.data?.responseCode == 0) {
      return true;
    } else {
      toast.error(data.message);
      return false;
    }
  } catch (err) {
    toast.error("Ocurrió un error al sincronizar la caja.");
    return false;
  }
};

// Función para sincronizar las órdenes pendientes
const syncOrders = async (shopCode, t) => {
  const orders = await indexedDbRepo.getOrders(shopCode);
  const ordersToSync = orders.filter(
    (order) =>
      order.asyncStatus == ASYNC_STATUS.PENDING &&
      (order.isPaid === true || order.movementType === ORDER_TYPE.VENTA) &&
      (order.status == ORDER_STATUS.CLOSED ||
      order.status == ORDER_STATUS.VOID ||
      order.status == ORDER_STATUS.VOID_OLD ||
      order.status == ORDER_STATUS.VOID_BY_BISTRO ||
      order.status == ORDER_STATUS.VOID_BY_BISTRO_OLD ||
      order.status == ORDER_STATUS.VOID_NT ||
      order.status == ORDER_STATUS.VOID_NT_OLD
    )
  );

  if (!navigator.onLine && ordersToSync.length > 0) {
    return toast.error(
      "La sincronización de órdenes se realizará cuando haya conexión a internet."
    );
  }

  let successfulSyncs = 0; // Contador de órdenes sincronizadas exitosamente
  for (const order of ordersToSync) {
    try {
      const isSuccess = await syncOrder(shopCode, order, t);

      if (isSuccess) {
        order.asyncStatus = ASYNC_STATUS.SYNCED;
        await updateOrder(shopCode, order);
        successfulSyncs++; // Incrementa el contador en caso de éxito
      }
    } catch (error) {
      toast.error(`Error al sincronizar la orden ${order.id}`);
    }
  }
  // Mostrar un único toast de éxito con el total de órdenes sincronizadas
  if (successfulSyncs > 0) {
    toast.success(`${successfulSyncs} orden(es) sincronizada(s) exitosamente.`);
  }
  await deleteOrders(shopCode);
};

// Función para sincronizar las cashbox pendientes
const syncCashboxes = async (shopCode, hasToShowToast = false) => {
  if(!navigator.onLine) return 
  const cashboxes = await cashboxIndexedDb.getCashboxes(shopCode);

  const cashboxesToSync = cashboxes.filter(
    (box) => box.asyncStatus == ASYNC_STATUS.PENDING
  );

  if (!navigator.onLine && cashboxesToSync.length > 0) {
    return toast.error(
      "La sincronización de movimientos de caja se realizará cuando haya conexión a internet."
    );
  }

  let successfulSyncs = 0; // Contador de movimientos sincronizados exitosamente
  for (const box of cashboxesToSync) {
    try {
      const isSuccess = await syncCashbox(shopCode, box);

      if (isSuccess) {
        box.asyncStatus = ASYNC_STATUS.SYNCED;
        await updateCashbox(shopCode, box);
        successfulSyncs++; // Incrementa el contador en caso de éxito
      }
    } catch (error) {
      toast.error(`Error al sincronizar el movimiento de caja ${box.id}`);
    }
  }
  // Mostrar un único toast de éxito con el total de movimientos sincronizados
  if (successfulSyncs > 0 && hasToShowToast) {
    toast.success(
      `${successfulSyncs} movimiento(s) de caja sincronizado(s) exitosamente.`
    );
  } 

  await deleteCashboxes(shopCode); 
};

const deleteOrders = async (shopCode) => {

  // Obtener la fecha de creación de la última caja de cierre
  const lastCashboxCreatedAt = await cashboxIndexedDb.getCreatedAtLastCashBox(
    shopCode
  );

  if (!lastCashboxCreatedAt) {
    return;
  }

  const orders = await indexedDbRepo.getOrders(shopCode);

  // Filtrar órdenes más antiguas que la fecha del último cierre de caja
  const ordersToDelete = orders.filter(
    (order) =>
      isOlderThan(order.createdAt, 90) &&
      order.createdAt < lastCashboxCreatedAt &&
      order.status == ORDER_STATUS.CLOSED &&
      order.asyncStatus == ASYNC_STATUS.SYNCED
  ); 

  for (const order of ordersToDelete) {
    await deleteOrder(shopCode, order);
  }
};

const deleteCashboxes = async (shopCode) => {
  const boxesBeforeLastClose =
    await cashboxIndexedDb.getMovementsCashboxToDelete(shopCode);

  // Filtrar órdenes más antiguas de 90 días entre las anteriores al cierre
  const boxesToDelete = boxesBeforeLastClose.filter(
    (box) =>
      isOlderThan(box.createdAt, 90) && box.asyncStatus == ASYNC_STATUS.SYNCED
  );

  for (const box of boxesToDelete) {
    await deleteCashbox(shopCode, box);
  }
};

export {
  getOrder,
  getOrders,
  getActiveOrders,
  getOrderByTableId,
  createOrder,
  updateOrder,
  createObjectCashbox,
  deleteOrder,
  syncCashboxes,
  getOrderByUuid,
  getReprintBillTicket,
  syncOrders,
  createVoidIndexDB,
  processBilling,
  processVoidBilling,
  replaceItemsTicket
};
